import { hooks, mui, React, ts, ui } from '_core';

import { DraftParams, Params } from './types';

type FormProps = {
  params: DraftParams;
  setParams: (_p: Params) => void;
};

const Form = ({ params, setParams }: FormProps) => {
  const [weights, setWeights] = React.useState(params.weights);
  const [window, setWindow] = React.useState(params.window);
  const [unlevered, setUnlevered] = React.useState(false);
  const [grossLeverage, setGrossLeverage] = React.useState<number>(params.gross_leverage);

  const handleGrossLeverageChange = (value: number) => {
    if (!isNaN(value)) {
      setGrossLeverage(value);
      if (value !== 1.0) {
        setUnlevered(false);
      }
    }
  };

  const handleUnleveredChange = () => {
    const newUnleveredValue = !unlevered;
    setUnlevered(newUnleveredValue);
    if (newUnleveredValue) {
      setGrossLeverage(1.0);
    }
  };

  const uiStyles = hooks.useUiStyles();

  React.useEffect(() => {
    if (weights !== ts.enums.REPORT_ENUMS.WEIGHTS_ENUM.MARKET_NEUTRAL) {
      setUnlevered(false);
      setGrossLeverage(params.gross_leverage); // Set to default or initial value
    }
  }, [weights]);

  React.useEffect(() => {
    const data = { ...params, weights, window, gross_leverage: grossLeverage };
    setParams(data);
  }, [weights, window, grossLeverage]);

  return (
    <mui.core.Box sx={uiStyles.widgetEditMode}>
      <mui.core.Grid container spacing={4}>
        <mui.core.Grid size={{ xs: 12, md: 6 }}>
          <ui.NumericField
            textFieldProps={{
              label: 'Forward Window (days)',
              fullWidth: true,
              variant: 'outlined',
              size: 'small',
              required: true,
            }}
            value={window}
            setValue={(v) => setWindow(v)}
          />
        </mui.core.Grid>
        <mui.core.Grid size={{ xs: 12, md: 6 }}>
          <mui.core.TextField
            select
            variant="outlined"
            size="small"
            label="Portfolio Weights"
            value={weights}
            onChange={(e) => setWeights(e.target.value as ts.enums.REPORT_ENUMS.WEIGHTS_ENUM)}
            fullWidth
            InputProps={{ style: { backgroundColor: 'white' } }}
            style={{ marginRight: '10px', marginTop: '2px', minWidth: '150px' }}
          >
            <mui.core.MenuItem value={ts.enums.REPORT_ENUMS.WEIGHTS_ENUM.MINIMUM_VOLATILITY}>
              Minimum Volatility
            </mui.core.MenuItem>
            <mui.core.MenuItem value={ts.enums.REPORT_ENUMS.WEIGHTS_ENUM.MARKET_CAP}>
              Market Capitalization
            </mui.core.MenuItem>
            <mui.core.MenuItem value={ts.enums.REPORT_ENUMS.WEIGHTS_ENUM.EQUAL}>Equal</mui.core.MenuItem>
            <mui.core.MenuItem value={ts.enums.REPORT_ENUMS.WEIGHTS_ENUM.MARKET_NEUTRAL}>
              Market Neutral
            </mui.core.MenuItem>
          </mui.core.TextField>
        </mui.core.Grid>
        <mui.core.Grid size={{ xs: 12, md: 12 }} container spacing={2}>
          {weights == ts.enums.REPORT_ENUMS.WEIGHTS_ENUM.MARKET_NEUTRAL && (
            <>
              <mui.core.Grid size={{ xs: 4 }}>
                <mui.core.FormControlLabel
                  control={<mui.core.Switch size="small" checked={unlevered} onChange={handleUnleveredChange} />}
                  label="Unlevered Portfolio"
                />
              </mui.core.Grid>
              <mui.core.Grid size={{ xs: 4 }}>
                <ui.NumericField
                  textFieldProps={{
                    label: 'Gross Leverage',
                    fullWidth: true,
                    variant: 'outlined',
                    size: 'small',
                    required: true,
                  }}
                  value={grossLeverage}
                  setValue={(v) => handleGrossLeverageChange(v)}
                />
              </mui.core.Grid>
            </>
          )}
        </mui.core.Grid>
      </mui.core.Grid>
    </mui.core.Box>
  );
};

export default Form;
